import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { retryHint } from '../lib/errors';

/* Keyed by the server's own error codes. `http_429` used to sit in this list
   and could never match: that key is only produced when a response has no
   JSON `error` field at all, and the rate limiter always sent one — first the
   plugin's English "Too Many Requests", now `too_many_requests`. So the one
   message written for the one case a person hits at shift change was dead
   from the day it was added, and the screen fell through to the generic
   "Přihlášení se nezdařilo." A wrong key is invisible; a wrong sentence is
   not. */
const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Nesprávný e-mail nebo heslo.',
  account_disabled: 'Účet je deaktivovaný. Kontaktujte správce.',
  invalid_request: 'Vyplňte prosím e-mail i heslo.',
  too_many_requests: 'Příliš mnoho pokusů o přihlášení.',
};

export function Login({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login({ email, password });
      onSuccess();
      navigate('/', { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'invalid_credentials';
      /* The wait is appended rather than written into the sentence because
         the server is the only thing that knows it, and it is the only part
         of a refusal the person can act on — the difference between waiting
         and telephoning the office is knowing it is forty seconds. */
      const hint = code === 'too_many_requests' ? retryHint(err instanceof ApiError ? err.retryAfter : undefined) : '';
      setError((MESSAGES[code] ?? 'Přihlášení se nezdařilo.') + (hint ? ` Zkuste to ${hint}.` : ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="loginwrap">
      <form className="card loginbox" onSubmit={submit}>
        <div className="brand">
          <div className="mark">SP</div>
          <div>
            <b>Smile Parking</b>
            <small>Extranet</small>
          </div>
        </div>

        <label htmlFor="email">E-mail</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <label htmlFor="password">Heslo</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Přihlašuji…' : 'Přihlásit se'}
        </button>

        {/* The one message on this page, and the one a screen reader will
            otherwise miss entirely: the field keeps focus, so nothing moves
            when the login is refused. */}
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
